import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Lock, Award, Loader2, Trophy, Dumbbell, Camera, Flame } from "lucide-react";

/**
 * Achievement badge strip. Reads /api/badges (earned) and /api/badges/tiers
 * (every configured tier across all families) and renders the 4 families
 * — streak / goals / workouts / photos — each as its own row.
 *
 * Props:
 *   - compact: bool — slim variant for the dashboard sidebar (single row
 *     showing whatever the most active family is, max 6 tiles)
 *   - title: string — section heading (default "Achievements")
 */
const FAMILY_META = {
  streak:   { label: "Streak",   icon: Flame,    suffix: "d",  color: "#FF7A2A" },
  goals:    { label: "Goals",    icon: Trophy,   suffix: "",   color: "#FFB020" },
  workouts: { label: "Workouts", icon: Dumbbell, suffix: "",   color: "#6B9BFF" },
  photos:   { label: "Photos",   icon: Camera,   suffix: "",   color: "#A06BFF" },
};

const FAMILIES = ["streak", "goals", "workouts", "photos"];

export default function BadgeStrip({ compact = false, title = "Achievements" }) {
  const [earned, setEarned] = useState(null); // {family: Map<tier, badge>}
  const [tiers, setTiers] = useState(null);   // {family: [{tier,label}]}

  const load = async () => {
    try {
      const [earnedRes, tiersRes] = await Promise.all([
        api.get("/badges"),
        api.get("/badges/tiers"),
      ]);
      const byFamily = {};
      FAMILIES.forEach((f) => (byFamily[f] = new Map()));
      (earnedRes.data || []).forEach((b) => {
        if (byFamily[b.type]) byFamily[b.type].set(b.tier, b);
      });
      setEarned(byFamily);
      setTiers(tiersRes.data || {});
    } catch {
      setEarned({});
      setTiers({});
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (earned === null || tiers === null) {
    return (
      <div
        className={`border border-white/10 bg-[#121212] ${compact ? "p-4" : "p-6"} flex items-center gap-2 text-white/40 text-sm`}
        data-testid="badge-strip-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading achievements…
      </div>
    );
  }

  const totalEarned = FAMILIES.reduce((n, f) => n + (earned[f]?.size || 0), 0);
  const totalTiers  = FAMILIES.reduce((n, f) => n + (tiers[f]?.length || 0), 0);

  // Compact mode picks the family the user is making the most progress in.
  const compactFamily = (() => {
    if (!compact) return null;
    let best = "streak";
    let bestSize = -1;
    FAMILIES.forEach((f) => {
      const size = earned[f]?.size || 0;
      if (size > bestSize) {
        best = f;
        bestSize = size;
      }
    });
    return best;
  })();

  const renderFamilyRow = (family) => {
    const meta = FAMILY_META[family];
    const Icon = meta.icon;
    const familyTiers = tiers[family] || [];
    const earnedMap = earned[family] || new Map();
    if (familyTiers.length === 0) return null;
    return (
      <div key={family} data-testid={`badge-family-${family}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
          <h4 className="label-eyebrow text-white/60 text-[10px]">{meta.label}</h4>
          <div className="text-[10px] uppercase tracking-widest text-white/30 ml-auto">
            {earnedMap.size} / {familyTiers.length}
          </div>
        </div>
        <div className={`grid gap-2 ${familyTiers.length >= 6 ? "grid-cols-3 sm:grid-cols-6" : "grid-cols-3"}`}>
          {familyTiers.map(({ tier, label }) => {
            const isEarned = earnedMap.has(tier);
            const color = meta.color;
            return (
              <div
                key={tier}
                className={`relative flex flex-col items-center justify-center text-center py-3 px-1 border transition-colors ${
                  isEarned
                    ? "border-current bg-current/10"
                    : "border-white/10 bg-[#0a0a0a] opacity-50"
                }`}
                style={isEarned ? { color, borderColor: color, backgroundColor: `${color}15` } : undefined}
                data-testid={`badge-tier-${family}-${tier}-${isEarned ? "earned" : "locked"}`}
                title={isEarned ? `Earned · ${label}` : `Locked · ${label}`}
              >
                {isEarned ? (
                  <Icon className="w-4 h-4 mb-1" style={{ color }} />
                ) : (
                  <Lock className="w-3.5 h-3.5 text-white/30 mb-1" />
                )}
                <div
                  className={`font-display text-base leading-none ${
                    isEarned ? "text-white" : "text-white/40"
                  }`}
                >
                  {tier}
                  {meta.suffix && (
                    <span className="text-[10px] text-white/40 ml-0.5">{meta.suffix}</span>
                  )}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 mt-1 leading-tight line-clamp-2">
                  {label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`border border-white/10 bg-[#121212] ${compact ? "p-4" : "p-6"}`}
      data-testid="badge-strip"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-[#6B9BFF]" />
          <h3 className={`label-eyebrow ${compact ? "text-white/70" : "text-white"}`}>{title}</h3>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-white/40" data-testid="badge-strip-count">
          {totalEarned} / {totalTiers}
        </div>
      </div>

      {compact ? (
        // Just the leading family — full strip lives on the Profile page.
        renderFamilyRow(compactFamily)
      ) : (
        <div className="space-y-5">
          {FAMILIES.map(renderFamilyRow)}
        </div>
      )}

      {totalEarned === 0 && !compact && (
        <p className="text-xs text-white/40 italic mt-5 text-center">
          Knock out a 3-day checklist streak, set your first goal, log your first set,
          or upload a progress photo to unlock your first badge.
        </p>
      )}
    </div>
  );
}
